import React, { useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

const STEPS = [
  {
    id: 1,
    stage: 'new',
    title: 'New Lead Comes In',
    color: '#3b82f6',
    icon: 'M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z',
    goal: 'Acknowledge the lead within 5 minutes. Speed-to-lead is the #1 factor in conversion.',
    actions: [
      { tool: 'Dashboard', text: 'Check your Dashboard for new leads — the "Urgent Leads" section shows anything that needs immediate attention.' },
      { tool: 'Quote Requests', text: 'Check Quote Requests for inbound website/phone estimates. Use the "Quick Convert" button to turn them into pipeline opportunities instantly.' },
      { tool: 'Lead Detail', text: 'Click into the lead. Read the customer notes, submission details, and any project description.' },
      { tool: 'AI Copilot', text: 'Hit "Summarize Customer" in the AI sidebar to get a quick profile of who they are and what they need.' },
    ],
    proTip: 'Leads contacted within 5 minutes are 21x more likely to convert than those contacted after 30 minutes. Make that first call fast.',
    nextStage: 'Contact Attempted',
  },
  {
    id: 2,
    stage: 'contact_attempted',
    title: 'First Contact Attempt',
    color: '#6366f1',
    icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    goal: 'Make first contact — call, text, or email. Log the attempt so the system tracks your responsiveness.',
    actions: [
      { tool: 'AI Call Prep', text: 'Before calling, hit "Prepare for Call" in the AI sidebar. It builds a briefing with talking points, cross-sell opportunities, and discovery questions.' },
      { tool: 'Log Activity', text: 'After the call attempt, click "Log Activity" and select call/text/email. Note whether you reached them or left a voicemail.' },
      { tool: 'Send Email', text: 'If no answer, send a quick introduction email. Use the "Introduction" template — it includes your signature and NTX branding.' },
    ],
    proTip: 'Always call first, then follow up with a text and email within 10 minutes. Triple-touch gets 3x more callbacks.',
    nextStage: 'Contacted',
  },
  {
    id: 3,
    stage: 'contacted',
    title: 'Customer Contacted',
    color: '#8b5cf6',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    goal: 'You spoke with them. Now gather project details and qualify the opportunity.',
    actions: [
      { tool: 'Clean Up Notes', text: 'After the conversation, paste your rough notes into "Clean Up Notes" in the AI sidebar. It will organize them, pull out action items, and format everything.' },
      { tool: 'Save & Re-Analyze', text: 'Click "Save Notes & Re-Analyze" — this saves the cleaned notes to the opportunity AND re-runs the Expansion Engine to find cross-sell opportunities based on what the customer told you.' },
      { tool: 'Edit Opportunity', text: 'Click the "Edit" button on the Opportunity sidebar. Update services, estimated value, and priority based on what you learned.' },
      { tool: 'Extract Project Data', text: 'Paste the customer description into "Extract Project Data." The AI pulls out footage, fence type, gates, acreage — everything the pricing engine needs.' },
    ],
    proTip: 'The more detail you capture now, the more accurate your estimate and the more relevant the AI cross-sell suggestions will be.',
    nextStage: 'Qualified',
  },
  {
    id: 4,
    stage: 'qualified',
    title: 'Qualify the Opportunity',
    color: '#f59e0b',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    goal: 'Confirm the customer is serious, has budget, and the project is real. Schedule the site visit.',
    actions: [
      { tool: 'Opportunity Health', text: 'Run "Opportunity Health" in the AI sidebar. It scores the deal and flags risks like "no budget discussed" or "timeline unclear."' },
      { tool: 'Suggest Next Steps', text: 'Click "Suggest Next Steps" — the AI recommends specific actions based on where the deal stands.' },
      { tool: 'Add Task', text: 'Schedule a site visit task with a due date. The system will remind you.' },
      { tool: 'Expansion Panel', text: 'Check the Expansion Engine panel. If it found cross-sell opportunities (like they need a gate for the new fence, or land clearing before fence installation), keep those in mind for the site visit.' },
    ],
    proTip: 'Ask about timeline and budget early. A customer who says "we want this done before summer" is 2x more likely to close than "someday."',
    nextStage: 'Site Visit',
  },
  {
    id: 5,
    stage: 'site_visit',
    title: 'Site Visit',
    color: '#f97316',
    icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
    goal: 'Walk the property. Take measurements, photos, and notes. Look for expansion opportunities naturally.',
    actions: [
      { tool: 'Site Visit Checklist', text: 'BEFORE you leave, click "Site Visit Checklist" in the AI sidebar. It generates a custom checklist for THIS specific project — measurements to take, photos needed, questions to ask, and expansion items to observe.' },
      { tool: 'Checklist on Site', text: 'Use the checklist on your phone while walking the property. Check items off as you go.' },
      { tool: 'Expansion Observations', text: 'The checklist includes "Observe for Additional Opportunities" items. These are natural observations — note the condition of existing gates, terrain that might need clearing, areas that could use concrete work.' },
      { tool: 'After Visit', text: 'Back at the office, use "Clean Up Notes" to organize your site visit observations. Save & Re-Analyze to update the expansion score.' },
    ],
    proTip: 'Take photos of EVERYTHING — property lines, existing structures, terrain, access points, utilities. Customers trust estimates backed by photos.',
    nextStage: 'Estimate In Progress',
  },
  {
    id: 6,
    stage: 'estimate_in_progress',
    title: 'Build the Estimate',
    color: '#ef4444',
    icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
    goal: 'Use the pricing engine and AI to build an accurate, professional estimate. The AI never calculates pricing — it feeds data into our deterministic pricing engine.',
    actions: [
      { tool: 'AI Estimate Generator', text: 'Go to the "Estimates" tab and use the AI Estimate Generator. It pulls from extracted project data and builds line items for the pricing engine.' },
      { tool: 'Edit Project Data', text: 'In the Expansion Panel, click "Edit" on Extracted Project Data to correct any footage, gate counts, or property details before generating the estimate.' },
      { tool: 'Review Cross-Sells', text: 'Check the Expansion Engine recommendations. If the customer mentioned additional services during the site visit, include them as optional line items in the estimate.' },
      { tool: 'PDF Quote', text: 'Generate the professional PDF quote with your NTX branding, itemized pricing, payment schedule (50/30/20), and e-signature field.' },
    ],
    proTip: 'Include 1-2 optional add-on items in the estimate (like gate automation or concrete). Customers who see options feel in control and often choose the middle tier.',
    nextStage: 'Estimate Sent',
  },
  {
    id: 7,
    stage: 'estimate_sent',
    title: 'Send the Estimate',
    color: '#ec4899',
    icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    goal: 'Deliver the estimate professionally. Set expectations for next steps.',
    actions: [
      { tool: 'Send Email', text: 'Click "Send Email" from the opportunity. Use the "Estimate Delivery" template. Attach the PDF quote.' },
      { tool: 'AI Email Writer', text: 'Use the AI Email Writer to craft a personalized cover message. It knows the project details and writes in NTX\'s voice.' },
      { tool: 'Add Task', text: 'Create a follow-up task for 2-3 business days out. Never send an estimate and just wait.' },
      { tool: 'Financing', text: 'If the project is over $2,000, include financing options. The Acorn Finance link is in your signature — mention it in the email.' },
    ],
    proTip: 'Call the customer 10 minutes after sending the email: "Hey, just sent over the estimate — wanted to make sure you got it and walk through anything." This dramatically increases close rates.',
    nextStage: 'Follow Up',
  },
  {
    id: 8,
    stage: 'follow_up',
    title: 'Follow Up',
    color: '#a855f7',
    icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    goal: 'Stay top-of-mind without being pushy. The AI helps you time and craft follow-ups.',
    actions: [
      { tool: 'AI Next Steps', text: 'Click "Suggest Next Steps" — the AI analyzes how long since the estimate was sent and recommends the right follow-up approach.' },
      { tool: 'AI Call Prep', text: 'Before each follow-up call, run "Prepare for Call." The briefing now includes cross-sell talking points from the Expansion Engine — natural conversation starters.' },
      { tool: 'Email Templates', text: 'Use the "Follow Up" email template for a professional check-in. The AI writer can personalize it based on the deal history.' },
      { tool: 'Discovery Questions', text: 'The Call Prep briefing includes Discovery Questions — use these to uncover additional needs or concerns the customer might have.' },
    ],
    proTip: 'Follow up 3 times minimum: Day 2, Day 5, Day 10. After that, switch to a monthly "just checking in" cadence. 80% of sales happen after the 5th contact.',
    nextStage: 'Customer Decision',
  },
  {
    id: 9,
    stage: 'customer_decision',
    title: 'Customer Decision',
    color: '#14b8a6',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    goal: 'The customer is deciding. Address objections, provide value, and make it easy to say yes.',
    actions: [
      { tool: 'Opportunity Health', text: 'Re-check Opportunity Health to identify any remaining risks or blockers.' },
      { tool: 'Edit Opportunity', text: 'Update the probability field to reflect your gut feel. This helps the dashboard forecast accuracy.' },
      { tool: 'Handle Objections', text: 'Common objections: price → offer financing or phased approach; timeline → show crew availability; competitor → highlight NTX quality and warranty.' },
      { tool: 'Expansion Value', text: 'If the customer is hesitating on price, DON\'T discount. Instead, show the added value of bundling services — "If we do the fence and the gate together, we can schedule it as one mobilization."' },
    ],
    proTip: 'Never ask "Have you made a decision?" Instead ask "What questions do you have about moving forward?" It assumes the sale and opens dialogue.',
    nextStage: 'Deposit Requested',
  },
  {
    id: 10,
    stage: 'deposit_requested',
    title: 'Request the Deposit',
    color: '#22c55e',
    icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
    goal: 'Close the deal. Collect the 50% deposit to lock in the project.',
    actions: [
      { tool: 'PDF Quote', text: 'Resend the final PDF quote from the Invoices tab. It includes the payment schedule: 50% deposit, 30% at materials delivery, 20% on completion.' },
      { tool: 'E-Signature', text: 'The quote has an e-signature field. Walk the customer through signing it on the phone if needed.' },
      { tool: 'Send Email', text: 'Send the deposit request email using the "Deposit Request" template with payment instructions.' },
      { tool: 'Log Activity', text: 'Log the deposit request. When the deposit arrives, move the opportunity to "Won."' },
    ],
    proTip: 'Make paying easy — offer multiple methods. The faster the deposit process, the less chance the customer changes their mind.',
    nextStage: 'Won',
  },
  {
    id: 11,
    stage: 'won',
    title: 'Deal Won!',
    color: '#16a34a',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    goal: 'Celebrate, then set up the project for success. Happy customers refer friends.',
    actions: [
      { tool: 'Move to Won', text: 'Click the "Won" stage button at the top. The system records your won value and updates all analytics.' },
      { tool: 'Thank You Email', text: 'Send a "Thank You" email confirming the project details and next steps for scheduling.' },
      { tool: 'Expansion Follow-Up', text: 'Check the Expansion Panel one more time — any "Future Project" items can be noted for follow-up after this project completes.' },
      { tool: 'Analytics', text: 'Check your Conversions and Expansion Analytics dashboards to see how your average ticket and win rate are trending.' },
    ],
    proTip: 'Ask for a Google review within 48 hours of project completion — that\'s when satisfaction is highest. Reviews drive 40% of new leads.',
    nextStage: null,
  },
];

const QUICK_REFS = [
  { title: 'AI Copilot Buttons', items: [
    { name: 'Opportunity Health', desc: 'Scores the deal strength and flags risks' },
    { name: 'Summarize Customer', desc: 'Quick profile of who they are and project history' },
    { name: 'Suggest Next Steps', desc: 'AI-recommended actions based on deal state' },
    { name: 'Prepare for Call', desc: 'Full briefing with talking points and cross-sell cues' },
    { name: 'Site Visit Checklist', desc: 'Custom checklist for this specific project visit' },
    { name: 'Clean Up Notes', desc: 'Paste rough notes, get organized + action items' },
    { name: 'Extract Project Data', desc: 'AI pulls footage, gates, acreage from text' },
  ]},
  { title: 'Expansion Engine', items: [
    { name: 'Auto-Analyze', desc: 'Runs automatically on new leads and when you update details' },
    { name: 'Expansion Score', desc: 'NONE / LOW / MEDIUM / HIGH / MAJOR PROJECT' },
    { name: 'Recommendations', desc: 'Logical cross-sell services with discovery questions' },
    { name: 'Edit Project Data', desc: 'Correct AI-extracted details when customer info changes' },
    { name: 'Feedback Buttons', desc: 'Mark each suggestion: Useful, Not Relevant, Added to Scope, etc.' },
  ]},
  { title: 'Email Power Moves', items: [
    { name: 'Templates', desc: 'Pre-built: Introduction, Estimate, Follow Up, Deposit Request' },
    { name: 'AI Writer', desc: 'Writes personalized emails using deal context and NTX voice' },
    { name: 'Attachments', desc: 'Attach PDFs, photos, and files directly to emails' },
    { name: 'Inbox', desc: 'Track all customer replies in one place' },
  ]},
];

export default function TutorialView() {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ntx_tutorial_progress') || '[]'); } catch { return []; }
  });
  const [showQuickRef, setShowQuickRef] = useState(false);
  const isMobile = useIsMobile();

  const toggleComplete = (stepId) => {
    const updated = completedSteps.includes(stepId)
      ? completedSteps.filter(s => s !== stepId)
      : [...completedSteps, stepId];
    setCompletedSteps(updated);
    localStorage.setItem('ntx_tutorial_progress', JSON.stringify(updated));
  };

  const progress = Math.round((completedSteps.length / STEPS.length) * 100);
  const step = STEPS[activeStep];

  return (
    <div data-testid="tutorial-view" style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '12px 10px' : '28px 32px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>
              NTX Sales Playbook
            </h1>
            <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
              Your step-by-step guide from lead to deposit. Follow this process to maximize conversions.
            </p>
          </div>
          <button data-testid="quick-ref-toggle" onClick={() => setShowQuickRef(!showQuickRef)}
            style={{ padding: '8px 16px', background: showQuickRef ? '#1e293b' : '#fff', color: showQuickRef ? '#fff' : '#1e293b',
              border: '1px solid #1e293b', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {showQuickRef ? 'Hide' : 'Show'} Quick Reference
          </button>
        </div>

        {/* Progress */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: progress === 100 ? '#16a34a' : '#c0392b', borderRadius: 4, transition: 'width .3s' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: progress === 100 ? '#16a34a' : '#475569', whiteSpace: 'nowrap' }}>
            {completedSteps.length}/{STEPS.length} steps reviewed
          </span>
        </div>
      </div>

      {/* Quick Reference Panel */}
      {showQuickRef && (
        <div data-testid="quick-reference" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          {QUICK_REFS.map((section, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: 16 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 10px', paddingBottom: 6, borderBottom: '2px solid #c0392b' }}>
                {section.title}
              </h3>
              {section.items.map((item, j) => (
                <div key={j} style={{ padding: '5px 0', borderBottom: j < section.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{item.desc}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? 12 : 20 }}>
        {/* Step Navigation */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', alignSelf: 'start', position: isMobile ? 'static' : 'sticky', top: 20 }}>
          <div style={{ padding: '14px 16px', background: '#1e293b', color: '#fff' }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>Sales Pipeline Steps</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Click each step to learn the process</div>
          </div>
          {isMobile ? (
            /* Mobile: Horizontal scrollable step selector */
            <div style={{ display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch', gap: 4, padding: 6 }}>
              {STEPS.map((s, i) => {
                const done = completedSteps.includes(s.id);
                const isActive = i === activeStep;
                return (
                  <button key={s.id} data-testid={`tutorial-step-${s.id}`} onClick={() => setActiveStep(i)}
                    style={{ padding: '8px 12px', border: 'none', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 36,
                      background: isActive ? s.color : done ? '#dcfce7' : '#f1f5f9', color: isActive ? '#fff' : done ? '#16a34a' : '#64748b' }}>
                    {s.title}
                  </button>
                );
              })}
            </div>
          ) : (
            /* Desktop: Vertical step list */
            <>
          {STEPS.map((s, i) => {
            const done = completedSteps.includes(s.id);
            const isActive = i === activeStep;
            return (
              <button key={s.id} data-testid={`tutorial-step-${s.id}`}
                onClick={() => setActiveStep(i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                  border: 'none', borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  background: isActive ? '#f8fafc' : '#fff', cursor: 'pointer', textAlign: 'left',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  background: done ? '#16a34a' : isActive ? s.color : '#e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 800, color: done || isActive ? '#fff' : '#94a3b8',
                }}>
                  {done ? '\u2713' : s.id}
                </div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? '#1e293b' : '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    {s.stage.replace(/_/g, ' ')}
                  </div>
                </div>
              </button>
            );
          })}
            </>
          )}
        </div>

        {/* Step Detail */}
        <div>
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {/* Step Header */}
            <div style={{ padding: isMobile ? '14px 14px' : '20px 24px', background: step.color, color: '#fff', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, flexWrap: 'wrap' }}>
              <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d={step.icon} />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Step {step.id} of {STEPS.length}</div>
                <h2 style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.title}</h2>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: 'rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: 6, flexShrink: 0, minHeight: 36 }}>
                <input type="checkbox" checked={completedSteps.includes(step.id)} onChange={() => toggleComplete(step.id)}
                  style={{ accentColor: '#16a34a', width: 16, height: 16 }} />
                <span style={{ fontSize: 12, fontWeight: 600 }}>Reviewed</span>
              </label>
            </div>

            {/* Goal */}
            <div style={{ padding: isMobile ? '12px 14px' : '16px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#c0392b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Goal</div>
              <p style={{ fontSize: 14, color: '#1e293b', margin: 0, lineHeight: 1.5, fontWeight: 500 }}>{step.goal}</p>
            </div>

            {/* Actions */}
            <div style={{ padding: isMobile ? '14px 14px' : '20px 24px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.3px' }}>What To Do</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {step.actions.map((action, i) => (
                  <div key={i} data-testid={`tutorial-action-${step.id}-${i}`}
                    style={{ display: 'flex', gap: 12, padding: '12px 14px', background: '#fafbff', borderRadius: 8, border: '1px solid #e0e7ff' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 6, background: step.color + '15', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 800, color: step.color,
                    }}>{i + 1}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: step.color, marginBottom: 2 }}>{action.tool}</div>
                      <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{action.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pro Tip */}
            <div style={{ padding: '0 24px 20px' }}>
              <div style={{
                padding: '14px 16px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a',
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <div style={{ width: 28, height: 28, background: '#f59e0b', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 2 }}>Pro Tip</div>
                  <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>{step.proTip}</div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbff' }}>
              <button data-testid="tutorial-prev" onClick={() => setActiveStep(Math.max(0, activeStep - 1))} disabled={activeStep === 0}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: '#fff', color: activeStep === 0 ? '#d1d5db' : '#475569', cursor: activeStep === 0 ? 'default' : 'pointer' }}>
                Previous
              </button>
              {step.nextStage && (
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  Next stage: <strong style={{ color: '#475569' }}>{step.nextStage}</strong>
                </div>
              )}
              <button data-testid="tutorial-next" onClick={() => setActiveStep(Math.min(STEPS.length - 1, activeStep + 1))} disabled={activeStep === STEPS.length - 1}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: activeStep === STEPS.length - 1 ? '#d1d5db' : '#c0392b', color: '#fff',
                  cursor: activeStep === STEPS.length - 1 ? 'default' : 'pointer' }}>
                {activeStep === STEPS.length - 1 ? 'Complete!' : 'Next Step'}
              </button>
            </div>
          </div>

          {/* Conversion Stats Card */}
          {activeStep === STEPS.length - 1 && completedSteps.length === STEPS.length && (
            <div data-testid="tutorial-complete" style={{ marginTop: 16, padding: '20px 24px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline' }}>
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" />
                </svg>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#16a34a', margin: '0 0 4px' }}>Playbook Complete</h3>
              <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>
                You've reviewed every step. Now go turn leads into deposits. The AI tools are here to help you move faster and sell smarter.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
